import React from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonContent,
  IonCard,
  IonCardContent,
  IonToggle,
  IonIcon,
  IonSpinner,
  IonText,
  IonAlert,
  IonModal,
  IonItem,
  IonInput,
  IonSelect,
  IonSelectOption,
  IonButton,
} from '@ionic/react';
import {
  trashOutline,
  addOutline,
  closeOutline,
  waterOutline,
  closeCircleOutline,
  thermometerOutline,
  compassOutline,
  rainyOutline,
  leafOutline,
  flowerOutline,
  colorFilterOutline,
} from 'ionicons/icons';
import { componentsSummary } from '../../domain/Component';
import {
  MAX_ALERT_CONDITIONS,
  stationAlertTemplates,
  alertableSensors,
  conditionKey,
  parseConditionKey,
  templateFits,
  WEATHER_METRICS,
  WEATHER_METRIC_INFO,
  LOT_STAGE_INFO,
  conditionInfo,
  describeCondition,
  alertTone,
  type AlertCondition,
  type AlertTone,
} from '../../domain/Alert';
import { useAlertsPage } from '../../application/use-cases/alerts/useAlertsPage';
import './Alerts.css';

const ALERT_TONE_ICON: Record<AlertTone, string> = {
  temp: thermometerOutline,
  humidity: waterOutline,
  wind: compassOutline,
  rain: rainyOutline,
  soil: leafOutline,
  stage: flowerOutline,
  spray: colorFilterOutline,
};

const Alerts: React.FC = () => {
  const {
    devices,
    deviceFilter,
    setDeviceFilter,
    alerts,
    loading,
    error,
    deletingAlertId,
    setDeletingAlertId,
    modalOpen,
    setModalOpen,
    formDevice,
    editingAlertId,
    formName,
    setFormName,
    formConditions,
    updateCondition,
    toggleStage,
    formLot,
    addCondition,
    removeCondition,
    applyTemplate,
    formError,
    saving,
    handleToggle,
    handleDelete,
    openCreate,
    openEdit,
    handleSubmit,
    visibleDevices,
    riegoNotifyValves,
    handleToggleNotifyOnSkip,
  } = useAlertsPage();

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/app/settings" />
          </IonButtons>
          <IonTitle>Alertas</IonTitle>
        </IonToolbar>
        {devices.length > 1 && (
          <IonToolbar>
            <IonSelect
              value={deviceFilter}
              placeholder="Dispositivo"
              interface="popover"
              onIonChange={(e) => setDeviceFilter(e.detail.value)}
              className="ion-padding-horizontal"
            >
              <IonSelectOption value="all">Todas las estaciones</IonSelectOption>
              {devices.map((d) => (
                <IonSelectOption key={d.id} value={d.id}>
                  {d.name}
                </IonSelectOption>
              ))}
            </IonSelect>
          </IonToolbar>
        )}
      </IonHeader>
      <IonContent fullscreen className="ion-padding">
        {loading && (
          <div className="ion-text-center ion-padding">
            <IonSpinner color="primary" />
          </div>
        )}

        {!loading && error && (
          <IonText color="danger">
            <p>{error}</p>
          </IonText>
        )}

        {!loading && !error && devices.length === 0 && (
          <p className="alerts-empty">
            Todavía no tenés estaciones con sensores para vigilar. Sumá una estación (o agregale sensores) para crear
            alertas.
          </p>
        )}

        {!loading &&
          !error &&
          visibleDevices.map((device) => {
            const deviceAlerts = alerts.filter((a) => a.deviceId === device.id);
            // Las alertas son de la estación: crearlas, editarlas o borrarlas
            // les cambia a todos y pide control. Prenderlas es de cada uno.
            const canEdit = device.access !== 'view';
            return (
              <div className="alert-device-group" key={device.id}>
                <p className="alert-device-title">
                  {device.name} · {componentsSummary(device.components)}
                </p>
                {device.access !== 'owner' && (
                  <p className="alert-shared-note">
                    Compartida por {device.ownerName}. Las alertas son las de la estación; vos elegís cuáles te avisan.
                  </p>
                )}
                <IonCard>
                  <IonCardContent>
                    {deviceAlerts.map((alert) => (
                      <div
                        className={`alert-row tone-${alertTone(alert.conditions[0])}${alert.triggered ? ' is-triggered' : ''}${alert.enabled ? '' : ' is-off'}`}
                        key={alert.id}
                      >
                        <span className="alert-icon">
                          <IonIcon icon={ALERT_TONE_ICON[alertTone(alert.conditions[0])]} />
                          {alert.triggered && <span className="alert-pulse" aria-hidden="true" />}
                        </span>
                        <button className="alert-row-main" disabled={!canEdit} onClick={() => openEdit(alert)}>
                          <span className="alert-direction">
                            {alert.name ?? alert.conditions.map((c) => describeCondition(c)).join(' y ')}
                          </span>
                          {alert.name && (
                            <span className="alert-conditions-note">
                              {alert.conditions.map((c) => describeCondition(c)).join(' y ')}
                            </span>
                          )}
                          {alert.triggered && <span className="alert-triggered-badge">Disparada ahora</span>}
                        </button>
                        <IonToggle
                          aria-label="Avisarme"
                          checked={alert.enabled}
                          onIonChange={(e) => handleToggle(alert, e.detail.checked)}
                        />
                        {canEdit && (
                          <button className="delete-icon-btn" onClick={() => setDeletingAlertId(alert.id)} aria-label="Eliminar alerta">
                            <IonIcon icon={trashOutline} />
                          </button>
                        )}
                      </div>
                    ))}
                    {canEdit && (
                      <button className="alert-add-row" onClick={() => openCreate(device)}>
                        <IonIcon icon={addOutline} />
                        Agregar alerta
                      </button>
                    )}
                  </IonCardContent>
                </IonCard>
              </div>
            );
          })}

        {!loading &&
          !error &&
          riegoNotifyValves.map((valve) => (
            <div className="alert-device-group" key={`${valve.device.id}:${valve.channel}`}>
              <p className="alert-device-title">
                {valve.device.name} · Riego · {valve.valveName}
              </p>
              <IonCard>
                <IonCardContent>
                  <div className="alert-row">
                    <div className="alert-row-main" style={{ flexDirection: 'row', alignItems: 'center' }}>
                      <IonIcon icon={waterOutline} style={{ marginRight: 8, flexShrink: 0 }} />
                      <span className="alert-direction">Avisarme si se saltea un riego programado</span>
                    </div>
                    <IonToggle
                      checked={valve.schedule.notifyOnSkip}
                      onIonChange={(e) => handleToggleNotifyOnSkip(valve, e.detail.checked)}
                    />
                  </div>
                </IonCardContent>
              </IonCard>
            </div>
          ))}

        <IonAlert
          isOpen={!!deletingAlertId}
          onDidDismiss={() => setDeletingAlertId(null)}
          header="¿Eliminar esta alerta?"
          message="Esta acción no se puede deshacer."
          buttons={[
            { text: 'Cancelar', role: 'cancel' },
            { text: 'Eliminar', role: 'destructive', handler: handleDelete },
          ]}
        />

        <IonModal isOpen={modalOpen} onDidDismiss={() => setModalOpen(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>
                {editingAlertId ? 'Editar alerta' : 'Nueva alerta'} · {formDevice?.name}
              </IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setModalOpen(false)}>
                  <IonIcon icon={closeOutline} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            {!editingAlertId &&
              formDevice &&
              stationAlertTemplates(formDevice.thresholds).some((t) => templateFits(t, formDevice.components)) && (
                <>
                  <p className="alert-form-section-title">Atajos</p>
                  <div className="alert-templates">
                    {stationAlertTemplates(formDevice.thresholds)
                      .filter((t) => templateFits(t, formDevice.components))
                      .map((t) => (
                        <button key={t.label} className="alert-template" onClick={() => applyTemplate(t)}>
                          {t.label}
                        </button>
                      ))}
                  </div>
                </>
              )}

            <IonItem className="alert-form-name">
              <IonInput
                label="Nombre (opcional)"
                labelPlacement="stacked"
                placeholder="Ej. Calor con viento"
                maxlength={60}
                value={formName}
                onIonInput={(e) => setFormName(e.detail.value ?? '')}
              />
            </IonItem>

            <p className="alert-form-section-title" style={{ marginTop: 16 }}>
              Avisarme cuando
            </p>
            {formConditions.map((draft, i) => {
              const { source, metric } = parseConditionKey(draft.key);
              const info = conditionInfo({ source, metric } as AlertCondition);
              return (
                <React.Fragment key={i}>
                  {i > 0 && <div className="alert-and">y</div>}
                  <div className="alert-condition">
                    <div className="alert-condition-top">
                      <IonSelect
                        aria-label="Variable"
                        interface="action-sheet"
                        value={draft.key}
                        onIonChange={(e) => updateCondition(i, { key: e.detail.value })}
                        className="alert-condition-metric"
                      >
                        {formDevice &&
                          alertableSensors(formDevice.components).map((a) => {
                            const key = conditionKey({
                              source: 'sensor',
                              ...a,
                            });
                            return (
                              <IonSelectOption key={key} value={key}>
                                {a.label}
                              </IonSelectOption>
                            );
                          })}
                        {WEATHER_METRICS.map((m) => (
                          <IonSelectOption key={m} value={`weather:${m}`}>
                            {WEATHER_METRIC_INFO[m].label}
                          </IonSelectOption>
                        ))}
                        {(formLot || source === 'lot') && (
                          <IonSelectOption value="lot:stage">{LOT_STAGE_INFO.label}</IonSelectOption>
                        )}
                      </IonSelect>
                      {formConditions.length > 1 && (
                        <button
                          className="alert-condition-remove"
                          onClick={() => removeCondition(i)}
                          aria-label="Quitar condición"
                        >
                          <IonIcon icon={closeCircleOutline} />
                        </button>
                      )}
                    </div>
                    {source === 'lot' ? (
                      <>
                        {formLot ? (
                          <div className="alert-stage-chips">
                            {formLot.stages.map((stage) => (
                              <button
                                key={stage.name}
                                className={draft.stages.includes(stage.name) ? 'active' : ''}
                                onClick={() => toggleStage(i, stage.name)}
                              >
                                {stage.name}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <p className="alert-form-hint">
                            La estación no está en ningún lote: la alerta queda en espera ({draft.stages.join(', ')}).
                          </p>
                        )}
                        <p className="alert-form-hint">
                          {formLot ? `Etapas de ${formLot.name}. ` : ''}
                          {LOT_STAGE_INFO.hint}
                        </p>
                      </>
                    ) : (
                      <div className="alert-condition-bottom">
                        <div className="alert-direction-toggle" role="group" aria-label="Dirección">
                          <button
                            className={draft.direction === 'above' ? 'active' : ''}
                            onClick={() => updateCondition(i, { direction: 'above' })}
                          >
                            mayor a
                          </button>
                          <button
                            className={draft.direction === 'below' ? 'active' : ''}
                            onClick={() => updateCondition(i, { direction: 'below' })}
                          >
                            menor a
                          </button>
                        </div>
                        <IonInput
                          aria-label="Umbral"
                          className="alert-condition-threshold"
                          inputmode="decimal"
                          placeholder="valor"
                          value={draft.threshold}
                          onIonInput={(e) =>
                            updateCondition(i, {
                              threshold: e.detail.value ?? '',
                            })
                          }
                        />
                        <span className="alert-condition-unit">{info.unit}</span>
                      </div>
                    )}
                    {source === 'weather' && (
                      <p className="alert-form-hint">Pronóstico de la ubicación de la estación, no medido por ella.</p>
                    )}
                    {info.hint && source !== 'lot' && <p className="alert-form-hint">{info.hint}</p>}
                  </div>
                </React.Fragment>
              );
            })}

            {formConditions.length < MAX_ALERT_CONDITIONS && (
              <button className="alert-add-condition" onClick={addCondition}>
                <IonIcon icon={addOutline} />
                Agregar condición (y)
              </button>
            )}
            <p className="alert-form-hint">
              Se dispara cuando se cumplen todas a la vez, y te llega una notificación al celular.
            </p>

            {formError && (
              <IonText color="danger">
                <p style={{ fontSize: 13 }}>{formError}</p>
              </IonText>
            )}

            <IonButton expand="block" style={{ marginTop: 16 }} onClick={handleSubmit} disabled={saving}>
              {saving ? 'Guardando...' : editingAlertId ? 'Guardar cambios' : 'Crear'}
            </IonButton>
          </IonContent>
        </IonModal>
      </IonContent>
    </IonPage>
  );
};

export default Alerts;
